import {
  ChatCustomBlockTypes,
  FsmDefMetaTypes,
  type BlockSteps,
  type ChatMessage,
  type FsmDefAxiosState,
  type FsmDefFormSchema,
  type FsmDefFormState,
  type FsmDefFormSuccessContent,
} from "@do/shared-types";
import { useMachine } from "@xstate/react";
import { useTranslations } from "next-intl";
import { useCallback, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { type AnyEventObject, createMachine } from "xstate";
import { objTemplateToObj, strTemplateToStr } from "../../../../lib/template-utils";
import { useChat } from "../../../../providers/chat-provider";
import { type StepValues } from "../../../../types/global";
import { FooterForm } from "../../../footer/footer-form";
import { MessageText } from "../text";
import { MessageStepAxios } from "./axios";
import { MessageStepDoBot } from "./do-bot";
import { MessageStepForm, type MessageStepFormProps } from "./form";
import {
  formatFormSuccessRecapValue,
  getFormSuccessTrigger,
  resolveFormSuccessContent,
  type FormSuccessBooleanLabels,
  type FormSuccessFieldRecap,
} from "./form-success";
import { MessageStepLiveChat } from "./livechat";
import { MessageStepMarkdown } from "./markdown";

type MessageStepsProps = {
  block: BlockSteps;
  messageIndex: number;
  messageId: string | undefined;
};

const getFormStateEntry = (fsmDef: BlockSteps["fsmDef"]) => {
  const entry = Object.entries(fsmDef.states).find(
    ([, state]) => state.meta.type === FsmDefMetaTypes.form
  );
  if (!entry) return null;

  const [stateKey, stateDef] = entry;
  return { stateKey, stateDef: stateDef as FsmDefFormState };
};

const buildRecapFields = (
  formSchema: FsmDefFormSchema | MessageStepFormProps["formSchema"],
  values: Record<string, unknown> | undefined,
  booleanLabels: FormSuccessBooleanLabels
): FormSuccessFieldRecap[] => {
  if (!values) return [];

  return Object.entries(formSchema.fields).flatMap(([fieldId, field]) => {
    if (field.type === "markdown") return [];
    if (!(fieldId in values)) return [];

    const value = formatFormSuccessRecapValue(values[fieldId], booleanLabels);
    if (!value) return [];

    const label =
      "props" in field && field.props && typeof field.props === "object" && "label" in field.props
        ? String(field.props.label ?? fieldId)
        : fieldId;

    return [{ id: fieldId, label, value }];
  });
};

const MessageStepsContent = ({ block, messageIndex, messageId }: MessageStepsProps) => {
  const t = useTranslations("do-chat-v2");
  const { chatbotParams, mergedCtxData, currentLocale, setMessages, scrollToBottom } = useChat();

  const machine = useMemo(() => createMachine(block.fsmDef), [block.fsmDef]);
  const [state, send] = useMachine(machine);

  // Memory of the FSM
  const [stepValues, setStepValues] = useState<StepValues>({});
  const successPublishedRef = useRef(false);
  // Keep latest step values without recreating wrappedSend (avoids axios effect re-firing).
  const stepValuesRef = useRef(stepValues);
  stepValuesRef.current = stepValues;

  const stateValue = state.value as keyof typeof FsmDefMetaTypes;
  const stateDef = block.fsmDef.states[stateValue];
  const context = { chatbotParams, ...stepValues, ...(mergedCtxData ?? {}) };
  const className = block.html_classes ?? "";
  const id = block.html_id ?? block.id;

  const formStateEntry = useMemo(() => getFormStateEntry(block.fsmDef), [block.fsmDef]);
  const formSchema = useMemo(() => {
    if (!formStateEntry) return null;
    return objTemplateToObj(
      formStateEntry.stateDef.meta.formSchema,
      context
    ) as MessageStepFormProps["formSchema"];
    // Templates in success copy rarely depend on step values; keep this stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStateEntry, chatbotParams, mergedCtxData]);

  const defaultLocaleKey = useMemo(() => {
    const locales = chatbotParams?.availableLocales ?? {};
    return Object.entries(locales).find(([, config]) => config?.isDefault)?.[0] ?? "fr_FR";
  }, [chatbotParams?.availableLocales]);

  const localeKey = currentLocale ?? defaultLocaleKey;

  const booleanLabels = useMemo(
    (): FormSuccessBooleanLabels => ({
      true: t("chat.messages.form-success.boolean-true"),
      false: t("chat.messages.form-success.boolean-false"),
    }),
    [t]
  );

  const publishSuccessMessage = useCallback(
    (content: FsmDefFormSuccessContent, values?: Record<string, unknown>) => {
      if (successPublishedRef.current || !formSchema) return;
      successPublishedRef.current = true;

      const recapFields = content.recapitulatif
        ? buildRecapFields(formSchema, values, booleanLabels)
        : [];
      const successMessageId = `form-success-${Date.now()}`;

      setMessages((previousMessages: ChatMessage[]) => [
        ...previousMessages,
        {
          id: successMessageId,
          user: false,
          blocks: [
            {
              id: `${successMessageId}-block`,
              type: ChatCustomBlockTypes.formSuccess,
              title: content.title,
              text: content.text,
              recapitulatif: content.recapitulatif
                ? { label: content.recapitulatif.label, fields: recapFields }
                : undefined,
              followingSteps: content.followingSteps,
              html_classes: className || undefined,
              html_id: id,
            },
          ],
        },
      ]);

      scrollToBottom?.({ force: true, smooth: true });
    },
    [booleanLabels, className, formSchema, id, scrollToBottom, setMessages]
  );

  const handleShowSuccess = useCallback(
    (content: FsmDefFormSuccessContent, values?: Record<string, unknown>) => {
      publishSuccessMessage(content, values);
    },
    [publishSuccessMessage]
  );

  // `onActionSuccess`: publish when a later state emits the FSM `success` event (e.g. axios).
  const wrappedSend = useCallback(
    (event: AnyEventObject) => {
      if (
        event.type === "success" &&
        !successPublishedRef.current &&
        formSchema?.success &&
        getFormSuccessTrigger(formSchema.success) === "onActionSuccess"
      ) {
        const content = resolveFormSuccessContent(formSchema.success, localeKey, defaultLocaleKey);
        if (content) {
          const values = formStateEntry
            ? (stepValuesRef.current[formStateEntry.stateKey] as
                | Record<string, unknown>
                | undefined)
            : undefined;
          publishSuccessMessage(content, values);
        }
      }
      send(event);
    },
    [defaultLocaleKey, formSchema?.success, formStateEntry, localeKey, publishSuccessMessage, send]
  );

  if (!stateDef) throw new Error(`Machine has no state definition for state ${stateValue}`);

  return (
    <ErrorBoundary
      fallback={
        <MessageText
          block={{
            type: ChatCustomBlockTypes.text,
            text: t("chat.messages.fsm-def-error"),
          }}
          messageId={messageId}
          messageIndex={messageIndex}
          id={`block-${id}-0`}
          idx={0}
          isUser={false}
          isErrorMessage
        />
      }
    >
      <div id={`${id}-container`}>
        {stateDef.meta.type === FsmDefMetaTypes.liveChat && (
          <MessageStepLiveChat
            liveChatConfigId={stateDef.meta.liveChatConfigId}
            payload={JSON.parse(strTemplateToStr(stateDef.meta.payloadTransformer, context))}
          />
        )}

        {stateDef.meta.type === FsmDefMetaTypes.markdown && (
          <MessageStepMarkdown
            className={className}
            id={id}
            text={strTemplateToStr(stateDef.meta.markdown, context)}
          />
        )}

        {stateDef.meta.type === FsmDefMetaTypes.doBot && (
          <MessageStepDoBot
            action={stateDef.meta.action}
            label={block.label}
            payload={strTemplateToStr(stateDef.meta.payloadTransformer, context)}
            solLabelId={block.sol_label_id}
          />
        )}

        {stateDef.meta.type === FsmDefMetaTypes.axios && (
          <MessageStepAxios
            onSetStepValue={setStepValues}
            options={objTemplateToObj(stateDef.meta.options, context)}
            send={wrappedSend}
            stateDef={stateDef as FsmDefAxiosState}
            stateValue={stateValue}
          />
        )}

        {stateDef.meta.type === FsmDefMetaTypes.form && (
          <FooterForm key={stateValue}>
            <MessageStepForm
              className={className}
              formSchema={
                objTemplateToObj(
                  stateDef.meta.formSchema,
                  context
                ) as MessageStepFormProps["formSchema"]
              }
              id={id}
              onSetStepValue={setStepValues}
              onShowSuccess={handleShowSuccess}
              send={wrappedSend}
              stateDef={stateDef as FsmDefFormState}
              stateValue={stateValue}
            />
          </FooterForm>
        )}
      </div>
    </ErrorBoundary>
  );
};

export const MessageSteps = ({ block, messageIndex, messageId }: MessageStepsProps) => {
  const { getMessageResetKey = () => 0 } = useChat();
  const messageResetKey = getMessageResetKey(messageId ?? block.id);
  const componentKey = `${
    messageId ?? block.id ?? `message-step-${messageIndex}`
  }-${messageResetKey}`;

  return (
    <MessageStepsContent
      key={componentKey}
      block={block}
      messageIndex={messageIndex}
      messageId={messageId}
    />
  );
};
